import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { uiModelApiService } from '../services/UIModelApiService';
import { CanvasAPI, ContainerAPI, UIModelAPI } from '../types/api.types';
import {  transformAPIToModel, transformContainersToAPI } from '../utils/modelTransformers';
import { useEditor } from './EditorContext';
import { Container } from '../types/canvas.types';
import {useModule} from "./ModuleContext";
import { generateComponentId } from '../utils/IdGenerator';
import {toast} from "react-toastify";
import {UIModelTheme} from "../components/ThemeEditor";

type UIModelApiContextType = {
    currentUIModelId: string | null;
    isLoading: boolean;
    error: string | null;
    createUIModel: (name: string, file: File | string | null, uiModelId?: string, isCopyModel?: boolean) => Promise<UIModelAPI>
    loadUIModel: (modelId: string, uiModelId: string) => Promise<void>;
    saveHeader: () => Promise<void>;
    saveFooter: () => Promise<void>;
    saveNavigation: () => Promise<void>;
    saveCanvas: (pageContainerRows?: {[pageId: string]: Record<string, number>}) => Promise<void>;
    headerConfig: any;
    setHeaderConfig: (config: any) => void;
    headerLogoFile: File | null;
    headerResetIconFile: File | null;
    headerCloseIconFile: File | null;
    setHeaderLogoFile: (file: File | null) => void;
    setHeaderResetIconFile: (file: File | null) => void;
    setHeaderCloseIconFile: (file: File | null) => void;
    footerConfig: any;
    setFooterConfig: (config: any) => void;
    footerPreviousIconFile: File | null;
    footerNextIconFile: File | null;
    footerDoneIconFile: File | null;
    setFooterPreviousIconFile: (file: File | null) => void;
    setFooterNextIconFile: (file: File | null) => void;
    setFooterDoneIconFile: (file: File | null) => void;
    navigationConfig: any;
    setNavigationConfig: (config: any) => void;
    canvasMap: { [pageId: string]: string };
    getGroupIdForPage: (pageId: string) => string;
    canvas: { [canvasId: string]: CanvasAPI };
    setCanvas: React.Dispatch<React.SetStateAction<{ [canvasId: string]: CanvasAPI }>>;
    createDefaultCanvas: (canvasId: string, pageId: string, groupId: string) => Promise<CanvasAPI>;
    setCanvasMap: React.Dispatch<React.SetStateAction<{
        [pageId: string]: string;
    }>>
    configureId: string | null;
    channelId: string;
    loadExistingUIModel: (modelId: string) => Promise<void>;
    deleteUIModel: (uiModelId: string) => Promise<boolean>;
    updateUIModel: (modelId: string, name: string, file: File | string | null) =>  Promise<UIModelAPI>;
    markPageAsModified: (pageId: string) => void
    getTheme: (projectId: string, modelId: string) => Promise<UIModelTheme>;
    updateTheme: (projectId: string, modelId: string, data: UIModelTheme | null) => Promise<boolean>;
}

const UIModelApiContext = createContext<UIModelApiContextType | undefined>(undefined);

export const UIModelApiProvider: React.FC<{ children: React.ReactNode; configureId: string; channelId: string; }> = ({ children, configureId, channelId }) => {
    const [currentUIModelId, setCurrentUIModelId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [canvasMap, setCanvasMap] = useState<{ [pageId: string]: string }>({});
    const [canvas, setCanvas] = useState<{ [canvasId: string]: CanvasAPI }>({});

    const [headerConfig, setHeaderConfig] = useState<any>(null);
    const [footerConfig, setFooterConfig] = useState<any>(null);
    const [navigationConfig, setNavigationConfig] = useState<any>(null);

    const { groups, containers, setGroups, setContainers, setUiModel, setEditorState, setCustomCSS, setTheme } = useEditor();
    const {setActiveView, hasEditPermission} = useModule();

    const headerConfigRef = useRef<any>(null);
    const footerConfigRef = useRef<any>(null);

    const [headerLogoFile, setHeaderLogoFile] = useState<File | null>(null);
    const [headerResetIconFile, setHeaderResetIconFile] = useState<File | null>(null);
    const [headerCloseIconFile, setHeaderCloseIconFile] = useState<File | null>(null);

    const [footerPreviousIconFile, setFooterPreviousIconFile] = useState<File | null>(null);
    const [footerNextIconFile, setFooterNextIconFile] = useState<File | null>(null);
    const [footerDoneIconFile, setFooterDoneIconFile] = useState<File | null>(null);

    const modifiedPagesRef = useRef<Set<string>>(new Set());

    const markPageAsModified = (pageId: string) => {
        modifiedPagesRef.current.add(pageId);
    };

    useEffect(() => {
        headerConfigRef.current = headerConfig;
    }, [headerConfig]);

    useEffect(() => {
        footerConfigRef.current = footerConfig;
    }, [footerConfig]);

    const setHeaderConfigWithRef = (config: any) => {
        headerConfigRef.current = config;
        setHeaderConfig(config);
    };

    const setFooterConfigWithRef = (config: any) => {
        footerConfigRef.current = config;
        setFooterConfig(config);
    };
  
    const createUIModel = async (name: string, file: File | string | null, uiModelId?: string, isCopyModel?: boolean): Promise<UIModelAPI> => {

      setIsLoading(true);
      setError(null);
      
      try {
        const formData = new FormData();
        if(file != null && file instanceof File) {
            formData.append("launchIcon", file, file.name);
        }
        if(file != null && typeof file === "string") {
            formData.append("launchIcon", file);
        }
        formData.append("channel_id", channelId);
        formData.append("name", name);
        let response;
        if(isCopyModel === true) {
            response = await uiModelApiService.copyUIModel(configureId, uiModelId ?? '', formData);
        } else {
            response = await uiModelApiService.createUIModel(configureId, formData);
        }
        if (response.success) {
          setCurrentUIModelId(response.data.model.id);
          
          const model = response.data.model;
          
          if (model.layout.header && model.layout.header.configuration) {
            setHeaderConfig(model.layout.header.configuration);
          }

          if (model.layout.footer && model.layout.footer.configuration) {
            setFooterConfig(model.layout.footer.configuration);
          }

          if (model.layout.navigation) {
            const { groups, ...navConfig } = model.layout.navigation;
            setNavigationConfig(navConfig);
          }
          
          const mapping: { [pageId: string]: string } = {};
          model.layout.navigation.groups.forEach(group => {
            group.pages.forEach(page => {
              if (page.canvasId) {
                mapping[page.id] = page.canvasId;
              }
            });
          });
          
          setCanvasMap(mapping);
          
          if (model.layout.canvas) {
            setCanvas(model.layout.canvas);
          }
          
          const internalGroups = model.layout.navigation.groups.map(group => ({
            id: group.id,
            name: group.name,
            order: group.order,
            orientation: group.orientation as 'horizontal' | 'vertical',
            configuration: {
              showTitle: group.configuration.showTitle
            },
            pages: group.pages.map(page => ({
              id: page.id,
              name: page.name,
              order: page.order,
              configuration: {
                autoAdvance: page.configuration.autoAdvance
              },
              canvas: { containers: [] }
            }))
          }));
          
          setGroups(internalGroups);
          
          if (model.editorState?.activeGroupId && model.editorState?.activePageId) {
            setEditorState(prev => ({
              ...prev,
              activeGroupId: model.editorState.activeGroupId,
              activePageId: model.editorState.activePageId,
              selectedContainerId: model.editorState.selectedContainerId || null
            }));
          } else if (internalGroups.length > 0 && internalGroups[0].pages.length > 0) {
            setEditorState(prev => ({
              ...prev,
              activeGroupId: internalGroups[0].id,
              activePageId: internalGroups[0].pages[0].id
            }));
          }
          
          const containerData: { [pageId: string]: Container[] } = {};
          
          for (const [pageId, canvasId] of Object.entries(mapping)) {
            if (model.layout.canvas && model.layout.canvas[canvasId]) {
              const canvasData = model.layout.canvas[canvasId];
              if (canvasData.containers) {
                containerData[pageId] = canvasData.containers.map(container =>
                  convertAPIContainerToInternal(container)
                );
              }
            }
          }
          
          setContainers(containerData);
          setUiModel(model);
          setActiveView('editor');
          toast.success('Successfully created UI Model')
          return response.data.model;
        }
        
        throw new Error('Failed to create model');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    const loadExistingUIModel = async (modelId: string): Promise<void> => {
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await uiModelApiService.getUIModelById(configureId, modelId);
        
        if (response.success) {
          const model: UIModelAPI = response.data.model;
          setCurrentUIModelId(model.id);
          
          setGroups([]);
          setContainers({});
          setCanvasMap({});
          setCanvas({});
          
          const mapping: { [pageId: string]: string } = {};
          
          model.layout.navigation.groups.forEach(group => {
            group.pages.forEach(page => {
              if (page.canvasId) {
                mapping[page.id] = page.canvasId;
              }
            });
          });
          
          setCanvasMap(mapping);
          
          if (model.layout.canvas) {
            setCanvas(model.layout.canvas);
          }
          
          if (model.layout.header?.configuration) {
            setHeaderConfig(model.layout.header.configuration);
          }

          if (model.layout.footer?.configuration) {
            setFooterConfig(model.layout.footer.configuration);
          }

          if (model.layout.navigation) {
            const nav = model.layout.navigation as any;

            const navConfig = {
              orientation: nav.orientation || 'Horizontal',
              style: nav.style || 'Block',
              flow: nav.flow || 'Nonlinear',
              textAlignment: nav.textAlignment || 'Centre',
              showDivider: nav.showDivider !== undefined ? nav.showDivider : true,
              showNavigation: nav.showNavigation !== undefined ? nav.showNavigation : true,
              pageSpacing: nav.pageSpacing || 10,
              groupSpacing: nav.groupSpacing || 10
            };

            setNavigationConfig(navConfig);
          }
          
          const internalGroups = model.layout.navigation.groups.map(group => ({
            id: group.id,
            name: group.name,
            order: group.order,
            orientation: group.orientation as 'horizontal' | 'vertical',
            configuration: {
              showTitle: group.configuration.showTitle
            },
            pages: group.pages.map(page => ({
              id: page.id,
              name: page.name,
              order: page.order,
              configuration: {
                autoAdvance: page.configuration.autoAdvance
              },
              canvas: { containers: [] }
            }))
          }));
          
          setGroups(internalGroups);
          
          const containerData: { [pageId: string]: Container[] } = {};
          
          const componentUsageMap: any = {};
          
          for (const [pageId, canvasId] of Object.entries(mapping)) {
            if (model.layout.canvas && model.layout.canvas[canvasId]) {
              const canvasData = model.layout.canvas[canvasId];
              if (canvasData.containers) {
                containerData[pageId] = canvasData.containers.map(container =>
                  convertAPIContainerToInternal(container)
                );
                
                const groupId = canvasData.groupId || '';
                
                canvasData.containers.forEach((container: any) => {
                  if (container.content?.submodel) {
                    componentUsageMap[container.content.submodel.id] = {
                      isUsed: true,
                      containerId: container.id,
                      pageId: pageId,
                      groupId: groupId
                    };
                  }
                  
                  if (container.content?.items) {
                    container.content.items.forEach((item: any) => {
                      componentUsageMap[item.id] = {
                        isUsed: true,
                        containerId: container.id,
                        pageId: pageId,
                        groupId: groupId
                      };
                      
                      if (item.parentId) {
                        componentUsageMap[item.parentId] = {
                          isUsed: true,
                          containerId: container.id,
                          pageId: pageId,
                          groupId: groupId
                        };
                      }
                    });
                  }
                });
              }
            }
          }
          
          if (model.editorState?.activeGroupId && model.editorState?.activePageId) {
            setEditorState(prev => ({
              ...prev,
              activeGroupId: model.editorState.activeGroupId,
              activePageId: model.editorState.activePageId,
              selectedContainerId: model.editorState.selectedContainerId || null,
              componentUsage: componentUsageMap
            }));
          } else if (internalGroups.length > 0 && internalGroups[0].pages.length > 0) {
            setEditorState(prev => ({
              ...prev,
              activeGroupId: internalGroups[0].id,
              activePageId: internalGroups[0].pages[0].id,
              componentUsage: componentUsageMap
            }));
          }
          
          setContainers(containerData);
          setUiModel(model);

          if (model.customCss) {
            setCustomCSS(model.customCss);
          }
          if (model.theme && !isEmpty(model.theme)) {
            setTheme(model.theme);
          } else {
              setTheme({
                  solidButtonBackground: "#0747A6",
                  solidButtonLabel: "#ffffff",
                  borderButtonBorder: "#0747A6",
                  borderButtonLabel: "#0747A6",
                  textButtonLabel: "#0747A6",
                  titleColor: "#0747A6",
                  subtitleColor: "#172B4D",
                  bodyTextColor: "#6D788C",
                  mainBackgroundColor: "#ffffff",
                  headerBackgroundColor: "#ffffff",
                  navigationBackgroundColor: "#ffffff"
              })
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        throw err;
      } finally {
        setIsLoading(false);
      }
  };

  const updateUIModel = async (modelId: string, name: string, file: File | string | null): Promise<UIModelAPI> => {
      if(!hasEditPermission()) {
          throw new Error('You dont have permission to update this model');
      }
      setIsLoading(true);
      setError(null);

      try {
          const formData = new FormData();
          if(file != null && file instanceof File) {
              formData.append("launchIcon", file, file.name);
          }
          if(file != null && typeof file === "string") {
              formData.append("launchIcon", file);
          }
          formData.append("channel_id", channelId);
          formData.append("name", name);
          const response = await uiModelApiService.updateUIModel(configureId, modelId, formData);

          if (response.success) {
              const model: UIModelAPI = response.data.model;
              setUiModel(model);
              return model;
          }
          throw new Error('Failed to create model');
      } catch (err) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          throw err;
      } finally {
          setIsLoading(false);
      }
  };
  
  const convertAPIContainerToInternal = (apiContainer: ContainerAPI, parentId?: string): Container => {
    const content = apiContainer.content || {};
    const hasSubmodel = content.submodel !== undefined;
    
    let type: 'widget' | 'subModel' | 'container' = 'widget';
    let componentId = 'empty';
    let widgetType = 'contentBlock';
    
    if (hasSubmodel) {
      type = 'subModel';
      componentId = content?.submodel?.id ?? 'empty';
    }

    const nestedContainers = content.nestedContainers?.map(
      nc => convertAPIContainerToInternal(nc, apiContainer.id)
    ) || [];

    const isNested = apiContainer.metadata?.isNested ?? !!parentId;
    
    return {
      id: apiContainer.id,
      name: apiContainer.name,
      order: apiContainer.order,
      configuration: apiContainer.configuration,
      metadata: {
        type: 'container',
        isNested: isNested,
        parentContainerId: apiContainer.metadata?.parentContainerId || parentId,
        rowIndex: !isNested && apiContainer.rowIndex !== undefined ? apiContainer.rowIndex : undefined
      },
      content: {
        type,
        componentId,
        'widget-type': widgetType,
        nestedContainers,
        submodel: content.submodel,
        items: content.items || []
      },
      usageInfo: apiContainer.usageInfo || { usedComponents: [] }
    };
  };
  
  const loadUIModel = async (modelId: string, uiModelId: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await uiModelApiService.getUIModel(modelId, uiModelId);
      
      if (response.success) {
        setCurrentUIModelId(uiModelId);

        const { model, containers: newContainers } = transformAPIToModel(response.data.model);
        setGroups(model.layout.groups);
        setContainers(newContainers);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  const saveHeader = async (): Promise<void> => {
    if(!hasEditPermission()) {
        throw new Error('You dont have permission to update this model');
    }
    const configToSave = headerConfigRef.current;

    if (!currentUIModelId || !configToSave || !configureId) {
        throw new Error('No UI Model, header config, or configure ID available');
    }

    setIsLoading(true);
    setError(null);

    try {

        await uiModelApiService.updateHeader(
            configureId,
            currentUIModelId,
            configToSave,
            headerLogoFile || undefined,
            headerResetIconFile || undefined,
            headerCloseIconFile || undefined
        );
    } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        throw err;
    } finally {
        setIsLoading(false);
    }
  };

  const saveFooter = async (): Promise<void> => {
    if(!hasEditPermission()) {
        throw new Error('You dont have permission to update this model');
    }
    const configToSave = footerConfigRef.current;

    if (!currentUIModelId || !configToSave || !configureId) {
        throw new Error('No UI Model, footer config, or configure ID available');
    }

    setIsLoading(true);
    setError(null);

    try {

        await uiModelApiService.updateFooter(
            configureId,
            currentUIModelId,
            configToSave,
            footerPreviousIconFile || undefined,
            footerNextIconFile || undefined,
            footerDoneIconFile || undefined
        );
    } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        throw err;
    } finally {
        setIsLoading(false);
    }
  };

  const saveNavigation = async (): Promise<void> => {
    if(!hasEditPermission()) {
        throw new Error('You dont have permission to update this model');
    }
    if (!currentUIModelId || !configureId) {
        throw new Error('No UI Model loaded, or configure ID not available');
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
        const apiGroups = groups.map(group => ({
        id: group.id,
        name: group.name,
        order: group.order,
        orientation: group.orientation,
        configuration: {
            showTitle: group.configuration.showTitle,
            name: group.name
        },
        pages: group.pages.map((page, index) => ({
            id: page.id,
            name: page.name,
            canvasId: canvasMap[page.id] || `canvas-${page.id}`,
            order: page.order,
            configuration: {
            autoAdvance: page.configuration.autoAdvance,
            name: page.name
            }
        }))
        }));
        
        const navigationData = {
        navigation: {
            orientation: navigationConfig?.orientation || 'Horizontal',
            style: navigationConfig?.style || 'Block',
            flow: navigationConfig?.flow || 'Nonlinear',
            textAlignment: navigationConfig?.textAlignment || 'Centre',
            showDivider: navigationConfig?.showDivider !== undefined ? navigationConfig.showDivider : true,
            showNavigation: navigationConfig?.showNavigation !== undefined ? navigationConfig.showNavigation : true,
            pageSpacing: navigationConfig?.pageSpacing || 10,
            groupSpacing: navigationConfig?.groupSpacing || 10,
            groups: apiGroups
        }
        };
        
        await uiModelApiService.updateNavigation(configureId, currentUIModelId, navigationData);
    } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        throw err;
    } finally {
        setIsLoading(false);
    }
  };
  
  const saveCanvas = async (pageContainerRows?: {[pageId: string]: Record<string, number>}) => {
    if(!hasEditPermission()) {
        throw new Error('You dont have permission to update this model');
    }
    if (!currentUIModelId || !configureId) {
        throw new Error('No UI Model loaded');
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
        const modifiedPages = Array.from(modifiedPagesRef.current);
        
        if (modifiedPages.length === 0) {
            console.log('No canvas changes to save');
            return;
        }

        const calculateRowFromLayout = (containers: Container[], currentIndex: number): number => {
            let currentRow = 0;
            let currentRowWidth = 0;
            
            for (let i = 0; i <= currentIndex; i++) {
                const container = containers[i];
                const width = parseFloat(container.configuration.width) || 100;
                
                if (currentRowWidth + width > 100.01 || width >= 100) {
                    if (i === currentIndex) {
                        return currentRow;
                    }
                    currentRow++;
                    currentRowWidth = width;
                } else {
                    currentRowWidth += width;
                }
            }
            
            return currentRow;
        };
        
        for (const pageId of modifiedPages) {
            const pageContainers = containers[pageId];
            if (!pageContainers) continue;
            
            const canvasId = canvasMap[pageId];
            if (!canvasId) {
                console.warn(`No canvas ID found for page ${pageId}`);
                continue;
            }
            
            const groupId = getGroupIdForPage(pageId);
            if (!groupId) {
                console.warn(`No group ID found for page ${pageId}`);
                continue;
            }
            
            const pageRows = pageContainerRows?.[pageId] || {};
            
            const completeRows: Record<string, number> = {};
            pageContainers.forEach((container, index) => {
                if (!container.metadata?.isNested) {
                    const containerId = container.id;
                    
                    if (pageRows && pageRows[containerId] !== undefined) {
                        completeRows[containerId] = pageRows[containerId];
                    }

                    else if (container.metadata?.rowIndex !== undefined) {
                        completeRows[containerId] = container.metadata.rowIndex;
                    }
                    else {
                        const calculatedRow = calculateRowFromLayout(pageContainers, index);
                        completeRows[containerId] = calculatedRow;
                    }
                }
            });
            
            
            const apiContainers = transformContainersToAPI(pageContainers, completeRows);
            
            const missingRowIndex = apiContainers.filter(c => c.rowIndex === undefined);
            if (missingRowIndex.length > 0) {
                console.error('CONTAINERS STILL MISSING ROWINDEX:', missingRowIndex.map(c => c.name));
            }

            const canvasPayload = {
                pageId: pageId,
                groupId: groupId,
                containers: apiContainers
            };

            const finalPayload = {
              canvas: {
                pageId: pageId,
                groupId: groupId,
                containers: apiContainers
              }
            };
            
            setCanvas(prev => ({
                ...prev,
                [canvasId]: canvasPayload
            }));
            
            await uiModelApiService.updateCanvas(configureId, currentUIModelId, finalPayload, canvasId);
        }
        
        modifiedPagesRef.current.clear();
        
      } catch (err) {
          console.error('Canvas save error:', err);
          setError(err instanceof Error ? err.message : 'Unknown error');
          throw err;
      } finally {
          setIsLoading(false);
      }
  };

  // const convertContainerToAPI = (container: Container): ContainerAPI => {
  //     const apiContainer: ContainerAPI = {
  //         id: container.id,
  //         name: container.name,
  //         order: container.order,
  //         configuration: container.configuration,
  //         content: {
  //             submodel: container.content.submodel,
  //             items: container.content.items || [],
  //             ...(container.content.nestedContainers && container.content.nestedContainers.length > 0 && {
  //                 nestedContainers: container.content.nestedContainers.map(nc => convertContainerToAPI(nc))
  //             })
  //         },
  //         usageInfo: container.usageInfo,
  //         ...(container.metadata?.rowIndex !== undefined && !container.metadata?.isNested && {
  //             rowIndex: container.metadata.rowIndex
  //         })
  //     };
  //   return apiContainer;
  // };

  const getGroupIdForPage = (pageId: string): string => {
    for (const group of groups) {
      if (group.pages.some(tab => tab.id === pageId)) {
        return group.id;
      }
    }
    return '';
  };

  const createDefaultCanvas = async (canvasId: string, pageId: string, groupId: string): Promise<CanvasAPI> => {
    const defaultContainer: ContainerAPI = {
      id: generateComponentId('container'),
      name: 'Container 1',
      order: 0,
      configuration: {
        width: '100%',
        height: 'auto',
        alignment: 'left',
        layout: 'vertical',
        showTitle: true,
        widthLocked: false,
        collapsible: false
      },
      content: {
        items: []
      },
      usageInfo: {
        usedComponents: []
      },
      rowIndex: 0
    };

    const defaultCanvas: CanvasAPI = {
      pageId: pageId,
      groupId: groupId,
      containers: [defaultContainer]
    };

    setCanvas(prev => ({
      ...prev,
      [canvasId]: defaultCanvas
    }));

    setCanvasMap(prev => ({
      ...prev,
      [pageId]: canvasId
    }));

    setContainers(prev => ({
      ...prev,
      [pageId]: [{
        id: defaultContainer.id,
        name: defaultContainer.name,
        order: defaultContainer.order,
        configuration: defaultContainer.configuration,
        content: {
          type: 'container',
          componentId: 'empty',
          'widget-type': '',
          nestedContainers: [],
          items: []
        },
        usageInfo: defaultContainer.usageInfo
      }]
    }));

    // Call API directly to persist the canvas (avoids React state timing issues)
    if (currentUIModelId && configureId) {
      try {
        const finalPayload = {
          canvas: {
            pageId: pageId,
            groupId: groupId,
            containers: [defaultContainer]
          }
        };
        await uiModelApiService.updateCanvas(configureId, currentUIModelId, finalPayload, canvasId);
      } catch (error) {
        console.error('Failed to save new canvas:', error);
      }
    }

    return defaultCanvas;
  };

  // const calculateRowFromLayout = (containers: Container[], currentIndex: number): number => {
  //   let currentRow = 0;
  //   let currentRowWidth = 0;
    
  //   for (let i = 0; i <= currentIndex; i++) {
  //     const container = containers[i];
  //     const width = parseFloat(container.configuration.width) || 100;
      
  //     if (currentRowWidth + width > 100.01 || width >= 100) {
  //       if (i === currentIndex) {
  //         return currentRow;
  //       }
  //       currentRow++;
  //       currentRowWidth = width;
  //     } else {
  //       currentRowWidth += width;
  //     }
  //   }
    
  //   return currentRow;
  // };

  const deleteUIModel = async (uiModelId: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await uiModelApiService.deleteUIModel(configureId, uiModelId);
      if (response.success) {
        return true;
      }
      throw new Error('Failed to delete model');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getTheme = async (projectId: string, modelId: string): Promise<UIModelTheme> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await uiModelApiService.getTheme(projectId, modelId);
      if (response.success) {
        if (response.data && !isEmpty(response.data)) {
            setTheme(response.data)
        }
        return response.data;
      }
      throw new Error('Failed to get theme');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

    function isEmpty(obj: object | null | undefined): boolean {
        return !obj || Object.keys(obj).length === 0;
    }

  const updateTheme = async (projectId: string, modelId: string, data: UIModelTheme | null): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await uiModelApiService.updateTheme(projectId, modelId, data);
      if (response.success) {
        return true;
      }
      throw new Error('Failed to update theme');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <UIModelApiContext.Provider
      value={{
        currentUIModelId,
        isLoading,
        error,
        createUIModel,
        loadUIModel,
        saveHeader,
        saveFooter,
        saveNavigation,
        saveCanvas,
        headerConfig,
        setHeaderConfig: setHeaderConfigWithRef,
        headerLogoFile,
        headerResetIconFile,
        headerCloseIconFile,
        setHeaderLogoFile,
        setHeaderResetIconFile,
        setHeaderCloseIconFile,
        footerConfig,
        setFooterConfig: setFooterConfigWithRef,
        footerPreviousIconFile,
        footerNextIconFile,
        footerDoneIconFile,
        setFooterPreviousIconFile,
        setFooterNextIconFile,
        setFooterDoneIconFile,
        navigationConfig,
        setNavigationConfig,
        canvasMap,
        getGroupIdForPage,
        canvas,
        setCanvas,
        createDefaultCanvas,
        setCanvasMap,
        configureId,
        channelId,
        loadExistingUIModel,
        deleteUIModel,
        updateUIModel,
        markPageAsModified,
        getTheme,
        updateTheme
      }}
    >
      {children}
    </UIModelApiContext.Provider>
  );
};

export const useUIModelApi = () => {
  const context = useContext(UIModelApiContext);
  if (!context) {
    throw new Error('useUIModelApi must be used within UIModelApiProvider');
  }
  return context;
};